import { useMemo } from 'react';

import { Trans, useTranslation } from '@lyearn/i18n';
import { ArrowIosDownFill } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Stack,
  Typography,
  useControlledState,
} from '@lyearn/ui';
import { QuestionTypeEnum } from '@/types/schema';

import { getQuestionLimitiedEditingInfo } from './config';

export interface LimitedEditingInfoProps {
  state?: { open: boolean; toggle: () => void };
  questionType: QuestionTypeEnum;
}

const summaryClasses = { content: '!m-0 items-center justify-between' };

const LimitedEditingInfo = (props: LimitedEditingInfoProps) => {
  const { t } = useTranslation();
  const { questionType, state } = props;
  const [open, toggle] = useControlledState({
    defaultValue: true,
    controlledValue: state?.open,
    onChange: state?.toggle,
  });

  const editingInfo = useMemo(
    () => getQuestionLimitiedEditingInfo({ t, questionType }),
    [questionType, t],
  );

  return (
    <Accordion
      className="!rounded-8 bg-bg-secondary shadow-none"
      expanded={open}
      onChange={(expanded) => toggle(expanded)}>
      <AccordionSummary className="!min-h-0 p-12 ps-16" classes={summaryClasses}>
        <Typography color="text-text-primary" variant="heading-xxs">
          {t('QuizCreator:LimitedEditingModal.Title')}
        </Typography>
        <IconButton
          appearance="secondary"
          shape="square"
          size="s"
          variant="ghost"
          onClick={() => toggle(!open)}>
          <ArrowIosDownFill
            className={cn('h-20 w-20 transform transition-transform', { '-rotate-90': !open })}
          />
        </IconButton>
      </AccordionSummary>
      <AccordionDetails className="p-0">
        {editingInfo.length ? (
          <Stack className="pb-16 pe-12 ps-16" direction="col" gapY={12}>
            {editingInfo.map(({ Icon, title }, index) => (
              <Stack key={index} gapX={10}>
                <Icon className="mt-2 h-16 w-16 shrink-0" fillColorToken="icon-primary" />
                <Typography color="text-text-paragraph" variant="body-short-02">
                  {title}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}
        <Typography
          className="border-t border-divider p-16"
          color="text-text-secondary"
          variant="body-short-03">
          <Trans
            i18nKey="QuizCreator:LimitedEditingModal.ChangesReflectedEverywhere"
            variant="label-03"
          />
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

export default LimitedEditingInfo;
